import React, { useState, useCallback } from 'react';
import {
  Card, Steps, Form, Input, Button, Space, Typography, Alert, Table,
  Tag, message, Modal, Result, Descriptions, Divider
} from 'antd';
import {
  LoginOutlined, MailOutlined, KeyOutlined, CheckCircleOutlined,
  CopyOutlined, DeleteOutlined, ReloadOutlined, SafetyCertificateOutlined,
  PlusOutlined
} from '@ant-design/icons';
import { aveniaApiKeyApi } from '../services/api';
import { getEnvByHostname } from '../utils/env';

const { Text, Paragraph } = Typography;

/** 统一取后端 { success, data, message } 结构 */
const unwrap = (res) => res?.data ?? {};

const copy = async (value, label) => {
  try {
    await navigator.clipboard.writeText(value);
    message.success(`${label} 已复制到剪贴板`);
  } catch {
    message.warning('浏览器拒绝了剪贴板访问，请手动选中复制');
  }
};

/**
 * Avenia API Key 申领向导。
 *
 * 为什么要这个页面：Avenia 注册 API Key 必须「账号密码 → 邮箱验证码 → JWT → 注册公钥」，
 * 账号密码和验证码只有财务有，而 apiKey 明文属于生产凭证，开发不该看到。
 * 财务在这里四步走完，把 apiKey 与私钥线下加密后交给开发即可。
 */
export default function AveniaApiKeyWizard() {
  const currentEnv = getEnvByHostname();
  const [current, setCurrent] = useState(0);
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [maskedEmail, setMaskedEmail] = useState('');
  const [mfaEnabled, setMfaEnabled] = useState(true);
  const [created, setCreated] = useState(null);

  const [keys, setKeys] = useState([]);
  const [maxCount, setMaxCount] = useState(5);
  const [listLoading, setListLoading] = useState(false);

  const [loginForm] = Form.useForm();
  const [validateForm] = Form.useForm();
  const [createForm] = Form.useForm();

  const refreshKeys = useCallback(async (sid) => {
    const id = sid || sessionId;
    if (!id) return;
    setListLoading(true);
    try {
      const body = unwrap(await aveniaApiKeyApi.list(id));
      if (body.success) {
        setKeys(body.data?.items || []);
        setMaxCount(body.data?.maxCount ?? 5);
      } else {
        message.error(body.message || '查询已注册 API Key 失败');
      }
    } catch (e) {
      message.error(e.response?.data?.message || '查询已注册 API Key 失败');
    } finally {
      setListLoading(false);
    }
  }, [sessionId]);

  const handleLogin = async (values) => {
    setLoading(true);
    try {
      const body = unwrap(await aveniaApiKeyApi.login(values));
      if (!body.success) {
        message.error(body.message || '登录失败');
        return;
      }
      setSessionId(body.data.sessionId);
      setMaskedEmail(body.data.maskedEmail);
      // 密码只在这一次请求里出现，立刻从表单里清掉，避免留在页面内存/回填
      loginForm.setFieldValue('password', '');
      message.success(`验证码已发送到 ${body.data.maskedEmail}`);
      setCurrent(1);
    } catch (e) {
      message.error(e.response?.data?.message || '登录失败');
    } finally {
      setLoading(false);
    }
  };

  const handleValidate = async (values) => {
    setLoading(true);
    try {
      const body = unwrap(await aveniaApiKeyApi.validate({ sessionId, ...values }));
      if (!body.success) {
        message.error(body.message || '验证码校验失败');
        return;
      }
      setMfaEnabled(body.data.mfaEnabled);
      validateForm.setFieldValue('emailToken', '');
      setCurrent(2);
      refreshKeys(sessionId);
    } catch (e) {
      message.error(e.response?.data?.message || '验证码校验失败');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (values) => {
    setLoading(true);
    try {
      const whitelistedIps = (values.whitelistedIps || '')
        .split(/[\s,;]+/)
        .filter(Boolean);
      const body = unwrap(await aveniaApiKeyApi.create({
        sessionId,
        name: values.name,
        otp: values.otp,
        whitelistedIps,
      }));
      if (!body.success) {
        message.error(body.message || '注册 API Key 失败');
        return;
      }
      setCreated(body.data);
      createForm.setFieldValue('otp', '');
      setCurrent(3);
      refreshKeys(sessionId);
    } catch (e) {
      message.error(e.response?.data?.message || '注册 API Key 失败');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (apiKeyId) => {
    let otp = '';
    Modal.confirm({
      title: '删除 API Key',
      icon: <DeleteOutlined style={{ color: '#ff4d4f' }} />,
      content: (
        <div>
          <Paragraph type="danger" style={{ marginBottom: 8 }}>
            删除后该 Key 立即失效，正在使用它的服务会全部鉴权失败。此操作不可撤销。
          </Paragraph>
          <Text type="secondary">请输入验证器 App 上的 6 位动态码确认：</Text>
          <Input
            style={{ marginTop: 8 }}
            maxLength={6}
            placeholder="TOTP 动态码"
            onChange={(e) => { otp = e.target.value; }}
          />
        </div>
      ),
      okText: '确认删除',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: async () => {
        if (!otp || otp.length !== 6) {
          message.error('请输入 6 位动态码');
          return Promise.reject();
        }
        const body = unwrap(await aveniaApiKeyApi.delete({ sessionId, apiKeyId, otp }));
        if (!body.success) {
          message.error(body.message || '删除失败');
          return Promise.reject();
        }
        message.success('API Key 已删除');
        refreshKeys(sessionId);
      },
    });
  };

  const handleReset = async () => {
    if (sessionId) {
      try { await aveniaApiKeyApi.logout(sessionId); } catch { /* 忽略 */ }
    }
    setSessionId(null);
    setCreated(null);
    setKeys([]);
    setMaskedEmail('');
    setCurrent(0);
    loginForm.resetFields();
    validateForm.resetFields();
    createForm.resetFields();
  };

  const keyColumns = [
    { title: '名称', dataIndex: 'name', width: 200 },
    {
      title: 'API Key ID',
      dataIndex: 'id',
      render: (v) => <Text code copyable style={{ fontSize: 12 }}>{v}</Text>,
    },
    {
      title: '算法',
      dataIndex: 'encryptionType',
      width: 90,
      render: (v) => v ? <Tag color="blue">{v}</Tag> : '-',
    },
    {
      title: '公钥指纹',
      dataIndex: 'fingerPrint',
      width: 220,
      render: (v) => <Text type="secondary" style={{ fontSize: 12 }}>{v || '-'}</Text>,
    },
    {
      title: 'IP 白名单',
      dataIndex: 'whitelistedIps',
      width: 160,
      render: (ips) => (ips || []).length
        ? ips.map((ip) => <Tag key={ip}>{ip}</Tag>)
        : <Tag>0.0.0.0/0</Tag>,
    },
    { title: '创建时间', dataIndex: 'createdAt', width: 200 },
    {
      title: '操作',
      width: 100,
      fixed: 'right',
      render: (_, row) => (
        <Button danger size="small" icon={<DeleteOutlined />} onClick={() => handleDelete(row.id)}>
          删除
        </Button>
      ),
    },
  ];

  const steps = [
    { title: '登录渠道后台', icon: <LoginOutlined /> },
    { title: '邮箱验证码', icon: <MailOutlined /> },
    { title: '生成并注册', icon: <KeyOutlined /> },
    { title: '保存凭证', icon: <CheckCircleOutlined /> },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card
        title={
          <Space>
            <SafetyCertificateOutlined />
            <span>Avenia API Key 申领</span>
            <Tag color={currentEnv === 'PROD' ? 'red' : 'green'}>{currentEnv}</Tag>
          </Space>
        }
        extra={sessionId && <Button icon={<ReloadOutlined />} onClick={handleReset}>重新开始</Button>}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
          message="这个页面做什么"
          description={
            <div>
              Avenia 注册 API Key 需要「账号密码 → 邮箱验证码 → 换取授权 → 注册公钥」四步，
              其中账号密码与验证码只有你持有。与渠道的交互统一由支付中台完成，本平台只做参数转发：
              <b>密码、验证码、授权令牌、apiKey 明文和私钥都不落库、不写日志</b>，
              apiKey 与私钥仅在第四步一次性展示，刷新页面即不可再得。
              请在第四步立即复制并线下加密保存后交给开发。
            </div>
          }
        />

        <Steps current={current} items={steps} style={{ marginBottom: 32 }} />

        {current === 0 && (
          <Form
            form={loginForm}
            layout="vertical"
            style={{ maxWidth: 520 }}
            onFinish={handleLogin}
          >
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message={`当前将通过 ${currentEnv} 环境的支付中台申领`}
              description="打到渠道沙箱还是生产，取决于本平台指向的支付中台环境，页面上无需也无法选择。"
            />
            <Form.Item
              name="email"
              label="渠道后台账号邮箱"
              rules={[{ required: true, message: '请输入邮箱' }, { type: 'email', message: '邮箱格式不正确' }]}
            >
              <Input placeholder="your.email@osl.com" autoComplete="off" />
            </Form.Item>
            <Form.Item
              name="password"
              label="渠道后台密码"
              extra="仅用于本次请求，服务端不保存、不写日志"
              rules={[{ required: true, message: '请输入密码' }]}
            >
              <Input.Password placeholder="渠道后台密码" autoComplete="new-password" />
            </Form.Item>
            <Button type="primary" htmlType="submit" loading={loading} icon={<LoginOutlined />}>
              登录并发送验证码
            </Button>
          </Form>
        )}

        {current === 1 && (
          <Form form={validateForm} layout="vertical" style={{ maxWidth: 520 }} onFinish={handleValidate}>
            <Alert
              type="success"
              showIcon
              style={{ marginBottom: 16 }}
              message={`验证码已发送到 ${maskedEmail}`}
              description="到邮箱取 6 位验证码填入下方。验证码有时效，过期请点「重新开始」再登录一次。"
            />
            <Form.Item
              name="emailToken"
              label="邮箱验证码"
              rules={[{ required: true, message: '请输入邮箱验证码' }]}
            >
              <Input placeholder="例如 874484" maxLength={12} autoComplete="off" />
            </Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading}>下一步</Button>
              <Button onClick={handleReset}>返回上一步</Button>
            </Space>
          </Form>
        )}

        {current === 2 && (
          <>
            {!mfaEnabled && (
              <Alert
                type="error"
                showIcon
                style={{ marginBottom: 16 }}
                message="该账号未启用 TOTP MFA"
                description="Avenia 要求先在渠道后台绑定验证器 App（TOTP）才能注册 API Key。请先完成绑定再回来操作。"
              />
            )}
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message={`每个账号最多 ${maxCount} 个 API Key，当前已有 ${keys.length} 个`}
              description="超过上限需先删除不用的 Key。密钥对由平台在服务端生成，只有公钥会发给 Avenia。"
            />
            <Form
              form={createForm}
              layout="vertical"
              style={{ maxWidth: 520 }}
              onFinish={handleCreate}
            >
              <Form.Item
                name="name"
                label="API Key 名称"
                extra="便于在渠道后台识别用途，如 osl-fiat-payment-prod"
                rules={[{ required: true, message: '请输入名称' }]}
              >
                <Input placeholder="osl-fiat-payment-prod" />
              </Form.Item>
              <Form.Item
                name="otp"
                label="TOTP 动态码"
                extra="来自验证器 App 的 6 位动态码"
                rules={[{ required: true, message: '请输入动态码' }, { len: 6, message: '动态码为 6 位' }]}
              >
                <Input placeholder="123456" maxLength={6} autoComplete="off" />
              </Form.Item>
              <Form.Item
                name="whitelistedIps"
                label="IP 白名单（CIDR，可选）"
                extra="限制的是「将来持这个 Key 发业务请求的机器」——即支付中台的出口 IP，不是本平台的。多个用空格或逗号分隔；留空表示不限制（等同 0.0.0.0/0）"
              >
                <Input placeholder="例如 10.0.0.0/8, 203.0.113.5/32" />
              </Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" loading={loading} icon={<PlusOutlined />}
                        disabled={keys.length >= maxCount}>
                  生成密钥对并注册
                </Button>
                <Button onClick={() => refreshKeys(sessionId)} icon={<ReloadOutlined />}>刷新列表</Button>
              </Space>
            </Form>
          </>
        )}

        {current === 3 && created && (
          <>
            <Result
              status="success"
              title="API Key 已注册"
              subTitle="以下内容只显示这一次，服务端不留存。请立即复制并线下加密保存。"
            />
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
              message="立即线下加密保存"
              description="离开或刷新本页后，apiKey 与私钥都无法再取回；只能删掉这个 Key 重新申领。不要通过明文聊天工具传输。"
            />
            <Descriptions bordered column={1} size="small" style={{ marginBottom: 16 }}>
              <Descriptions.Item label="名称">{created.name}</Descriptions.Item>
              <Descriptions.Item label="算法">{created.keyAlgorithm}</Descriptions.Item>
              <Descriptions.Item label="apiKey（X-API-Key 请求头的值）">
                <Space>
                  <Text code>{created.apiKey}</Text>
                  <Button size="small" icon={<CopyOutlined />}
                          onClick={() => copy(created.apiKey, 'apiKey')}>复制</Button>
                </Space>
              </Descriptions.Item>
            </Descriptions>

            <Divider orientation="left">私钥（用于请求签名，绝不外传）</Divider>
            <Input.TextArea value={created.privateKeyPem} readOnly rows={8}
                            style={{ fontFamily: 'monospace', fontSize: 12 }} />
            <Button style={{ marginTop: 8 }} icon={<CopyOutlined />}
                    onClick={() => copy(created.privateKeyPem, '私钥')}>复制私钥</Button>

            <Divider orientation="left">公钥（已注册到渠道，仅供核对）</Divider>
            <Input.TextArea value={created.publicKeyPem} readOnly rows={6}
                            style={{ fontFamily: 'monospace', fontSize: 12 }} />
            <Button style={{ marginTop: 8 }} icon={<CopyOutlined />}
                    onClick={() => copy(created.publicKeyPem, '公钥')}>复制公钥</Button>

            <div style={{ marginTop: 24 }}>
              <Space>
                <Button type="primary" onClick={() => { setCreated(null); setCurrent(2); }}>
                  再申领一个
                </Button>
                <Button onClick={handleReset}>结束并清除会话</Button>
              </Space>
            </div>
          </>
        )}

        {sessionId && current >= 2 && (
          <>
            <Divider orientation="left">已注册的 API Key</Divider>
            <Table
              rowKey="id"
              size="small"
              loading={listLoading}
              dataSource={keys}
              columns={keyColumns}
              pagination={false}
              scroll={{ x: 1100 }}
            />
          </>
        )}
      </Card>
    </div>
  );
}
